// 🔒 MULTI-BRANCH-FASE2-2026-05 — Selector de sucursal en el header.
//
// Comportamiento:
//   - Modo single (multi_branch_enabled=false) → no se renderiza nada.
//   - Modo multi:
//       · 1 sola sucursal permitida → muestra chip readonly (sin dropdown).
//       · 2+ sucursales → dropdown shadcn.
//       · admin/propietario → además opción "Todas" (aggregate / lectura).
//
// Persistencia y propagación via BranchContext. El axios interceptor de
// lib/api.js añade el header X-Branch-Id en cada request automáticamente.
import { useBranches } from '@/context/BranchContext';
import { Building2, MapPin, ChevronDown, LayoutGrid } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';

export default function BranchSelector() {
  const {
    multiBranchEnabled, branches, activeBranchId, isAggregate,
    canUseAggregate, setActiveBranchId, aggregateKey, mainBranch,
  } = useBranches();

  if (!multiBranchEnabled) return null;
  if (!branches || branches.length === 0) return null;

  const active = branches.find(b => b.id === activeBranchId);
  const label = isAggregate
    ? 'Todas'
    : (active?.name || mainBranch?.name || 'Sucursal');
  const code = isAggregate ? null : (active?.code || mainBranch?.code);

  // Si hay solo 1 sucursal permitida y el usuario NO puede usar aggregate,
  // mostrar un chip readonly (sin dropdown).
  if (branches.length === 1 && !canUseAggregate) {
    const only = branches[0];
    return (
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted text-foreground text-sm font-medium"
        data-testid="branch-selector-readonly"
        title={only.address || only.name}
      >
        <MapPin size={14} className="text-primary" />
        <span className="truncate max-w-[120px]">{only.name}</span>
        {only.code && (
          <Badge variant="secondary" className="text-[10px] font-mono">{only.code}</Badge>
        )}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/15 text-foreground text-sm font-medium transition-colors min-h-[36px]"
          data-testid="branch-selector-trigger"
          title={active?.address || label}
        >
          {isAggregate ? (
            <LayoutGrid size={14} className="text-primary" />
          ) : (
            <MapPin size={14} className="text-primary" />
          )}
          <span className="truncate max-w-[140px]">{label}</span>
          {code && (
            <Badge variant="secondary" className="text-[10px] font-mono">{code}</Badge>
          )}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]" data-testid="branch-selector-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Sucursal activa
        </DropdownMenuLabel>
        {branches.map(b => (
          <DropdownMenuItem
            key={b.id}
            onClick={() => setActiveBranchId(b.id)}
            className="gap-2 cursor-pointer"
            data-testid={`branch-option-${b.id}`}
          >
            <MapPin
              size={14}
              className={b.id === activeBranchId ? 'text-primary' : 'text-muted-foreground'}
            />
            <span className="flex-1 truncate">{b.name}</span>
            {b.is_main && (
              <Badge variant="outline" className="text-[9px] py-0">Principal</Badge>
            )}
            {b.code && (
              <Badge variant="secondary" className="text-[10px] font-mono">{b.code}</Badge>
            )}
          </DropdownMenuItem>
        ))}
        {canUseAggregate && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setActiveBranchId(aggregateKey)}
              className="gap-2 cursor-pointer"
              data-testid="branch-option-all"
            >
              <LayoutGrid
                size={14}
                className={isAggregate ? 'text-primary' : 'text-muted-foreground'}
              />
              <span className="flex-1">Todas (consolidado)</span>
              <Building2 size={12} className="opacity-50" />
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
